/**
 * Composable for handling status badges consistently across components
 * Provides status classes, labels, and colors for various entity types
 */

export const useStatusBadge = () => {
  /**
   * Get status badge classes for machines
   * @param {string} status - Machine status
   * @returns {string} CSS classes for the status badge
   */
  const getMachineStatusClasses = (status) => {
    const classes = {
      published: 'status-badge-success',
      sold: 'status-badge-error',
      reserved: 'status-badge-warning',
      draft: 'status-badge-neutral',
      archived: 'status-badge-neutral',
    };
    return classes[status] || classes.draft;
  };

  /**
   * Get status label for machines (matches backend MachineStatus::publicLabel())
   * @param {string} status - Machine status
   * @returns {string} Human-readable status label for visitors
   */
  const getMachineStatusLabel = (status) => {
    // Must match backend: app/Enums/MachineStatus.php -> publicLabel()
    const labels = {
      published: 'Available',
      sold: 'Sold',
      reserved: 'Reserved',
      draft: 'Coming Soon',
      archived: 'Unavailable',
    };
    return labels[status] || 'Unknown';
  };

  /**
   * Get availability badge classes for machines
   * @param {string} availability - Machine availability (immediately, in_production, sold)
   * @returns {string} CSS classes for the availability badge
   */
  const getMachineAvailabilityClasses = (availability) => {
    const classes = {
      immediately: 'status-badge-success',
      in_production: 'status-badge-info',
      sold: 'status-badge-error',
    };
    return classes[availability] || classes.immediately;
  };

  /**
   * Get availability label for machines (matches backend MachineAvailability::publicLabel())
   * @param {string} availability - Machine availability
   * @param {string|null} availableFrom - Available from date (ISO string)
   * @returns {string} Human-readable availability label for visitors
   */
  const getMachineAvailabilityLabel = (availability, availableFrom = null) => {
    // If in production with a date, show the date
    if (availability === 'in_production' && availableFrom) {
      const date = new Date(availableFrom);
      const options = { month: 'short', year: 'numeric' };
      return `In Production - Available ${date.toLocaleDateString('en-US', options)}`;
    }

    // Must match backend: app/Enums/MachineAvailability.php -> publicLabel()
    const labels = {
      immediately: 'Available Now',
      in_production: 'In Production',
      sold: 'Sold',
    };
    return labels[availability] || 'Available Now';
  };

  /**
   * Get status badge classes for spare parts
   * @param {number} stockQuantity - Current stock quantity
   * @param {number} minimumStock - Minimum stock threshold
   * @returns {string} CSS classes for the status badge
   */
  const getSparePartStatusClasses = (stockQuantity, minimumStock = 0) => {
    if (stockQuantity <= 0) return 'status-badge-error';
    if (stockQuantity <= minimumStock) return 'status-badge-warning';
    return 'status-badge-success';
  };

  /**
   * Get status label for spare parts
   * @param {number} stockQuantity - Current stock quantity
   * @param {number} minimumStock - Minimum stock threshold
   * @returns {string} Human-readable status label
   */
  const getSparePartStatusLabel = (stockQuantity, minimumStock = 0) => {
    if (stockQuantity <= 0) return 'Out of Stock';
    return 'In Stock';
  };

  /**
   * Get status badge classes for general statuses
   * @param {string} status - General status
   * @returns {string} CSS classes for the status badge
   */
  const getGeneralStatusClasses = (status) => {
    const classes = {
      success: 'status-badge-success',
      warning: 'status-badge-warning',
      error: 'status-badge-error',
      info: 'status-badge-info',
      neutral: 'status-badge-neutral',
    };
    return classes[status] || classes.neutral;
  };

  /**
   * Get urgency level classes for quote requests
   * @param {string} urgency - Urgency level
   * @returns {string} CSS classes for the urgency badge
   */
  const getUrgencyClasses = (urgency) => {
    const classes = {
      low: 'status-badge-success',
      medium: 'status-badge-warning',
      high: 'status-badge-error',
      critical: 'status-badge-error',
    };
    return classes[urgency] || classes.low;
  };

  /**
   * Get urgency level label
   * @param {string} urgency - Urgency level
   * @returns {string} Human-readable urgency label
   */
  const getUrgencyLabel = (urgency) => {
    const labels = {
      low: 'Low - Standard delivery',
      medium: 'Medium - Expedited delivery',
      high: 'High - Rush delivery',
      critical: 'Critical - Same day/Next day',
    };
    return labels[urgency] || 'Standard delivery';
  };

  /**
   * Get budget range classes
   * @param {string} budgetRange - Budget range
   * @returns {string} CSS classes for the budget badge
   */
  const getBudgetRangeClasses = (budgetRange) => {
    const classes = {
      '0-1000': 'status-badge-success',
      '1000-5000': 'status-badge-info',
      '5000-10000': 'status-badge-warning',
      '10000-50000': 'status-badge-warning',
      '50000+': 'status-badge-error',
    };
    return classes[budgetRange] || 'status-badge-neutral';
  };

  /**
   * Get budget range label
   * @param {string} budgetRange - Budget range
   * @returns {string} Human-readable budget label
   */
  const getBudgetRangeLabel = (budgetRange) => {
    const labels = {
      '0-1000': '€0 - €1,000',
      '1000-5000': '€1,000 - €5,000',
      '5000-10000': '€5,000 - €10,000',
      '10000-50000': '€10,000 - €50,000',
      '50000+': '€50,000+',
    };
    return labels[budgetRange] || 'Not specified';
  };

  return {
    getMachineStatusClasses,
    getMachineStatusLabel,
    getMachineAvailabilityClasses,
    getMachineAvailabilityLabel,

    getSparePartStatusClasses,
    getSparePartStatusLabel,

    getGeneralStatusClasses,

    getUrgencyClasses,
    getUrgencyLabel,
    getBudgetRangeClasses,
    getBudgetRangeLabel,
  };
};
